import { crush } from '@tedconf/js-crushinator-helpers';
import Link from 'next/link';
import { FormattedMessage } from 'react-intl';

import Avatar from 'components/avatar';
import ChevronLink from 'components/chevron-link';
import Divider from 'components/divider';
import { Heading, Text } from 'components/typography';

import type SpeakerDetailsProps from './SpeakerDetails.props';

const SpeakerProfile = ({
  published,
  slug,
  children
}: {
  published: boolean;
  slug: string;
  children: React.ReactNode;
}) => {
  if (!published) return children;

  return (
    <Link
      href={`/speakers/${slug}`}
      target="_blank"
      rel="noreferrer noopener"
      className="flex items-start"
    >
      {children}
    </Link>
  );
};

const SpeakerDetails = ({
  speakers,
  videoContext,
  isPartnerContent = false
}: SpeakerDetailsProps) => {
  if (!speakers?.length) {
    return null;
  }

  const checkSpeakerType = () => {
    if (isTEDEdLesson) {
      return (
        <FormattedMessage
          defaultMessage="Meet the {speakerCount, plural,
              one {educator}
              other {educators}
            }"
          values={{ speakerCount: speakers.length }}
        />
      );
    }
    if (isPartnerContent) {
      return (
        <FormattedMessage
          defaultMessage="About the partner"
          values={{ speakerCount: speakers.length }}
        />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="About the {speakerCount, plural,
        one {speaker}
        other {speakers}
      }"
        values={{ speakerCount: speakers.length }}
      />
    );
  };

  const isTEDEdLesson = videoContext.toLowerCase() === 'ted-ed';
  return (
    <div className="w-full">
      <div className="hidden lg:block">
        <Heading size="m" tagName="h2">
          {checkSpeakerType()}
        </Heading>

        <Divider className="mb-4 mt-1" />
      </div>

      {speakers.map(
        ({
          photoUrl,
          firstname,
          lastname,
          description,
          slug,
          whoTheyAre,
          isLive
        }) => {
          const avatarURL = `${crush(photoUrl)}`;

          return (
            <div key={slug}>
              <div className="flex flex-col justify-between md:flex-row">
                <SpeakerProfile published={isLive} slug={slug}>
                  <div className="flex">
                    {avatarURL && (
                      <div className="mr-2 w-14">
                        <Avatar
                          src={avatarURL}
                          alt={`${firstname} ${lastname}`}
                          className="h-14 w-14 rounded-full"
                          imgWidth={56}
                        />
                      </div>
                    )}

                    <div>
                      <Text
                        size="l"
                        className="font-bold"
                      >{`${firstname} ${lastname}`}</Text>
                      <Text size="s" className="text-gray-500">
                        {isTEDEdLesson && !description
                          ? 'Educator'
                          : description}
                      </Text>
                    </div>
                  </div>
                </SpeakerProfile>

                {isLive && (
                  <ChevronLink
                    text="See speaker profile"
                    className="mt-2 items-start text-blue-300 hover:text-blue-500 md:mt-0"
                    url={`/speakers/${slug}`}
                    newTab
                  />
                )}
              </div>
              <div className="mb-6 mt-3">
                <Text
                  size="m"
                  className="text-black"
                  {...{
                    dangerouslySetInnerHTML: { ...{ __html: whoTheyAre } }
                  }}
                />
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default SpeakerDetails;
